import { type ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { bookedAlready } from "../../src/booking.ts";
import { ApiError, api, type Item, type TripView } from "./api.ts";
import {
  bookingKind,
  changed,
  type Draft,
  draftOf,
  editOf,
  freeStep,
  type Group,
  type Line,
  newLine,
  type Problem,
  rebase,
  tripZone,
} from "./bookingDraft.ts";
import { currencyList, Field, PaymentFields, Select, withValue, YourCurrency } from "./fields.tsx";
import { itemWhen, personName } from "./format.ts";
import { asLabel, everyone } from "./former.tsx";
import { useKept, useOnline } from "./offline.ts";
import { paidMoney } from "./paid.ts";
import { ReportThis } from "./ReportThis.tsx";
import { Button, Icon, SegmentedControl } from "./souvenir.ts";
import { type Lookup, loadLookup, zoneAt } from "./tzLookup.ts";
import { deviceZone } from "./zone.ts";

// The booking form (DESIGN.md, Views, "Record a booking where it's made"; features/bookings-and-costs.md, "Record
// a booking"): behind "Mark as booked" or "Edit booking" on the card of a stay, a journey, a choice's option, or a
// place that needs booking or is booked, for anyone in the trip. It opens over the card, as the card opens over a
// view: the card's own panel on a computer, a sheet over it on a phone (TripPage). It holds when (a stay's
// check-in and check-out, a journey's departure and arrival each in its own time zone, a place's date and time),
// the price, each payment (who, how much, and how: Card, Cash, Points, Bank transfer, or a few words of my own),
// booked through, its reference and link, who booked it (me, unless changed), free cancellation until, and the
// confirmation, attached once it's saved. Saving is one write, signed "on the web", undoable at once (the trip's
// notice, "Booked … · Undo") and from Updates, over the item as the form read it: if someone changed it meanwhile,
// nothing is written over, and the form shows theirs. Without signal it says saving needs signal. On something
// booked, a quiet "Not booked after all" at its foot puts a booking marked by mistake back to needing booking,
// keeping what's filled in (DESIGN.md, "What the web adds, it can take away"); cancelling stays the AI's.

const MAX_BYTES = 20 * 1024 * 1024;
const CONFIRMATION_TYPES = "application/pdf,image/jpeg,image/png,image/webp,image/heic,.heic";

// What the form leaves when it closes: the change that saved it (none: nothing changed) and what to say of it.
export interface Saved {
  change: string | null;
  text: string;
}

// Time zones as people find them in a list: by their city, its offset beside it, around the item's date.
function zoneOptions(at: Date): { value: string; label: string }[] {
  let zones: string[];
  try {
    zones = (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf("timeZone");
  } catch {
    zones = [];
  }
  return zones
    .map((zone) => {
      const city = (zone.split("/").pop() ?? zone).replace(/_/g, " ");
      let offset = "";
      try {
        offset =
          new Intl.DateTimeFormat("en", { timeZone: zone, timeZoneName: "shortOffset" })
            .formatToParts(at)
            .find((p) => p.type === "timeZoneName")?.value ?? "";
      } catch {}
      return { value: zone, label: offset ? `${city} (${offset})` : city };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}
let zonesFor: { day: string; options: { value: string; label: string }[] } | null = null;
const zoneList = (day: string) => {
  if (zonesFor?.day !== day) zonesFor = { day, options: zoneOptions(new Date(`${day}T12:00:00Z`)) };
  return zonesFor.options;
};

export function BookingForm({
  item,
  trip,
  items,
  bar,
  onClose,
  onChanged,
  onSaved,
  saving,
  at = null,
}: {
  item: Item;
  trip: TripView;
  items: Item[];
  // On a computer, its head's close (back to the card); on a phone the sheet's bar has it.
  bar?: ReactNode;
  onClose: () => void;
  // Saved: load the trip again, and close once it has the item at `version` (TripPage), so the card under it
  // never shows it as it was. Meanwhile (`saving`), saving shows; a form opened again in its place shows so too.
  onChanged: () => void;
  onSaved: (saved: Saved, version: number) => void;
  saving: boolean;
  // Opened at its Not booked after all (Change with your AI), rather than from its heading.
  at?: "unbook" | null;
}) {
  const kind = bookingKind(item);
  const people = useMemo(() => everyone(trip), [trip]);
  const online = useOnline();
  const kept = useKept();
  const offline = !online || kept !== null;
  // Each end's time zone where the item has none: its place's, else the trip's.
  const [lookup, setLookup] = useState<Lookup | null>(null);
  useEffect(() => {
    let live = true;
    loadLookup().then((l) => live && l && setLookup(() => l));
    return () => {
      live = false;
    };
  }, []);
  const zones = (from: Item, l: Lookup | null) => {
    const fallback = tripZone(items, deviceZone());
    const at = (end: boolean) => zoneAt(from, l, end);
    const start = at(false) ?? fallback;
    return { start, end: from.type === "move" ? (at(true) ?? start) : start };
  };
  const yours = useContext(YourCurrency);
  const currencies = currencyList(
    [
      item.cost?.currency,
      ...(item.paid ?? []).map((p) => p.currency),
      trip.money.budget?.currency,
      trip.money.estimate?.currency,
      yours,
      ...items.flatMap((i) => [i.cost?.currency, ...(i.paid ?? []).map((p) => p.currency)]),
    ].filter((c): c is string => Boolean(c)),
  );
  const defaults = (me: string | null) => ({ currency: currencies[0] ?? "USD", me, people });
  // The item as the form read it (`base`: its version, and the form as it was), and the form as I've typed it.
  const [base, setBase] = useState(() => ({ item, draft: draftOf(item, zones(item, null), defaults(null)) }));
  // Who booked it: me, when I'm the one marking it booked; an edit leaves it as it is (nobody named stays so).
  // An option with no when of its own starts from its choice's ("Osaka, nights 10-19"), saved onto it as booked.
  const [draft, setDraft] = useState<Draft>(() => {
    const choice = items.find((i) => i.id === item.parent && i.type === "choice");
    const from = !item.when && choice?.when ? { ...item, when: choice.when } : item;
    return draftOf(from, zones(item, null), defaults(bookedAlready(item) ? null : trip.you));
  });
  // Once the zones' table is in: an end's zone the item didn't have, from its place, unless I've changed it.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, when the table arrives
  useEffect(() => {
    if (!lookup) return;
    const z = zones(base.item, lookup);
    const fix = (d: Draft, was: Draft): Draft => ({
      ...d,
      startTz: d.startTz === was.startTz && !hasZone(base.item) ? z.start : d.startTz,
      endTz: d.endTz === was.endTz && !hasZone(base.item) ? z.end : d.endTz,
    });
    setDraft((d) => fix(d, base.draft));
    setBase((b) => ({ ...b, draft: fix(b.draft, b.draft) }));
  }, [lookup]);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Which of the two writes is on its way, or was last made (the form shows "Saving…" on its own button).
  const [doing, setDoing] = useState<"save" | "unbook">("save");
  // Someone's change meanwhile: who, and the parts where theirs now shows.
  const [meanwhile, setMeanwhile] = useState<{ who: string; theirs: Group[] } | null>(null);
  const [confirmation, setConfirmation] = useState<File | null>(null);

  const fileInput = useRef<HTMLInputElement>(null);
  const form = useRef<HTMLFormElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const policy = freeStep(base.item);
  // Booked already (someone may have booked it while this was open): its booking to edit.
  const marking = !bookedAlready(base.item);
  // Booked, not done yet: it can be not booked after all.
  const unbookable = base.item.status === "booked";

  // Opened: read from its heading, from the top (on a computer the card's panel was wherever the card was
  // scrolled to; closing puts it back there, and the focus on the button that opened it); or, opened at it, from
  // its Not booked after all.
  // biome-ignore lint/correctness/useExhaustiveDependencies: once, as it opens
  useEffect(() => {
    const scroller = form.current?.closest<HTMLElement>(".tm-panel, .tm-modal-body");
    const top = scroller?.scrollTop ?? 0;
    if (scroller) scroller.scrollTop = 0;
    const unbook = at === "unbook" ? form.current?.querySelector<HTMLElement>(".tm-not-booked") : null;
    if (unbook) {
      unbook.scrollIntoView({ block: "center" });
      unbook.focus({ preventScroll: true });
    } else heading.current?.focus({ preventScroll: true });
    return () => {
      requestAnimationFrame(() => {
        if (scroller?.isConnected) scroller.scrollTop = top;
        document.querySelector<HTMLElement>(".tm-book-button")?.focus({ preventScroll: true });
      });
    };
  }, []);

  const set = (patch: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    const fields = Object.keys(patch);
    setProblems((ps) => ps.filter((p) => !fields.includes(p.field)));
  };
  const setLine = (key: string, patch: Partial<Line>) => {
    setDraft((d) => ({ ...d, lines: d.lines.map((l) => (l.key === key ? { ...l, ...patch } : l)) }));
    const fields = Object.keys(patch).map((k) => `${key}.${k}`);
    setProblems((ps) => ps.filter((p) => !fields.includes(p.field)));
  };
  const problem = (field: string) => problems.find((p) => p.field === field)?.message ?? null;
  // Paid in full by me: one payment, mine, of the whole price (for everyone it's for, a price per person).
  const count = draft.per === "person" ? item.booking?.for?.length || trip.members.length : 1;
  const priceAmount = Number(draft.amount.replace(/[,\s]/g, ""));
  const whole = draft.amount.trim() && Number.isFinite(priceAmount) && priceAmount > 0 ? priceAmount * count : null;
  const paidInFull = () =>
    set({
      lines: [
        newLine({
          by: trip.you,
          amount: String(Math.round((whole ?? 0) * 100) / 100),
          currency: draft.currency,
          method: draft.lines.find((l) => l.by === trip.you)?.method ?? "",
        }),
      ],
    });

  const save = async () => {
    if (offline || saving) return;
    setError(null);
    const made = editOf(kind, draft, base.draft, base.item.version, Boolean(base.item.cost?.estimate));
    if (made.edit === null) {
      setProblems(made.problems);
      const first = made.problems[0];
      if (first) form.current?.querySelector<HTMLElement>(`[data-field="${CSS.escape(first.field)}"]`)?.focus();
      return;
    }
    setBusy(true);
    setDoing("save");
    try {
      const { change, version } = await api.book(trip.id, item.id, made.edit);
      let failed: string | null = null;
      if (confirmation) {
        try {
          await api.attachFile(trip.id, item.id, confirmation);
        } catch (e) {
          failed = `The confirmation didn't attach: ${e instanceof Error ? e.message : "attach it on its card"}`;
        }
      }
      const done = change
        ? `${marking ? "Booked" : "Saved the booking of"} ${item.title}`
        : confirmation && !failed
          ? `Attached the confirmation to ${item.title}`
          : "";
      onChanged();
      onSaved({ change, text: [done, failed].filter(Boolean).join(". ") }, change ? version : 0);
    } catch (e) {
      setBusy(false);
      if (meetTheirs(e)) return;
      setError(e instanceof Error ? e.message : "Couldn't save it. Try again.");
    }
  };
  // Not booked after all: back to needing booking, what's filled in kept for next time, over the item as read.
  const notBooked = async () => {
    if (offline || saving || busy) return;
    setError(null);
    setBusy(true);
    setDoing("unbook");
    try {
      const { change, version } = await api.notBooked(trip.id, item.id, base.item.version);
      onChanged();
      onSaved({ change, text: `Not booked after all: ${item.title}` }, version);
    } catch (e) {
      setBusy(false);
      if (meetTheirs(e)) return;
      setError(e instanceof Error ? e.message : "Couldn't change it. Try again.");
    }
  };
  // Someone changed it meanwhile: nothing saved, theirs shown where we both changed something, said at the top.
  const meetTheirs = (e: unknown): boolean => {
    if (!(e instanceof ApiError && e.status === 409 && e.data["item"])) return false;
    const now = e.data["item"] as Item;
    const was = base.draft;
    const nowDraft = draftOf(now, zones(now, lookup), defaults(null));
    const { draft: merged, theirs } = rebase(draft, was, nowDraft);
    const by = now.updated?.by;
    setBase({ item: now, draft: nowDraft });
    setDraft(merged);
    setMeanwhile({ who: by?.id === trip.you ? "You" : asLabel(by?.name ?? "Someone"), theirs });
    // From the top, where it says so.
    const scroller = form.current?.closest<HTMLElement>(".tm-panel, .tm-layer-body, .tm-modal-body");
    if (scroller) scroller.scrollTop = 0;
    return true;
  };
  const savingNow = (busy || saving) && doing === "save";

  const others = trip.members.filter((m) => m.id !== trip.you).sort((a, b) => a.name.localeCompare(b.name));
  const me = trip.members.find((m) => m.id === trip.you);
  // Who can have paid or booked it: me first, then the others; someone already named who has since left too.
  const whoOptions = (current: string) => [
    ...(me ? [{ value: me.id, label: `You (${me.name})` }] : []),
    ...others.map((m) => ({ value: m.id, label: m.name })),
    ...(current && !trip.members.some((m) => m.id === current)
      ? [{ value: current, label: asLabel(personName(current, people, trip.you) ?? "Someone who left") }]
      : []),
  ];
  const day = draft.startDate || new Date().toISOString().slice(0, 10);
  const zoneSelect = (field: "startTz" | "endTz", label: string, words: string) => (
    <Field label={label} field={field} problem={null}>
      {(id) => (
        <Select
          id={id}
          field={field}
          aria-label={words}
          value={draft[field]}
          onChange={(v) => set({ [field]: v })}
          options={withValue(zoneList(day), draft[field])}
        />
      )}
    </Field>
  );
  const dateTime = (which: "start" | "end", label: string, timeLabel: string, words: string) => {
    const date = which === "start" ? "startDate" : "endDate";
    const time = which === "start" ? "startTime" : "endTime";
    return (
      <div className="tm-booking-pair">
        <Field label={label} field={date} problem={problem(date)}>
          {(id) => (
            <input
              id={id}
              data-field={date}
              className={`sv-field-input${draft[date] ? "" : " is-empty"}`}
              type="date"
              value={draft[date]}
              aria-invalid={problem(date) ? true : undefined}
              onChange={(e) => set({ [date]: e.target.value })}
            />
          )}
        </Field>
        <Field label={timeLabel} field={time} problem={problem(time)}>
          {(id) => (
            <input
              id={id}
              data-field={time}
              className={`sv-field-input${draft[time] ? "" : " is-empty"}`}
              type="time"
              aria-label={words}
              value={draft[time]}
              aria-invalid={problem(time) ? true : undefined}
              onChange={(e) => set({ [time]: e.target.value })}
            />
          )}
        </Field>
      </div>
    );
  };
  // What it has now that the fields can't say (a part of a day, days not yet dates).
  const now = item.when && !["exact", "day", "days"].includes(item.when.precision) ? itemWhen(item.when) : null;
  const changedBy = (g: Group) =>
    meanwhile?.theirs.includes(g) ? (
      <p className="caption tm-booking-theirs">
        {meanwhile.who === "You" ? "You changed this elsewhere" : `${meanwhile.who} changed this meanwhile`}.
      </p>
    ) : null;

  return (
    <form
      ref={form}
      className="tm-booking"
      aria-labelledby={`booking-${item.id}`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
      onKeyDown={(e) => {
        if (e.key !== "Escape") return;
        // Back to the card, before the page's own Escape (which closes the card).
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }}
    >
      {bar && <div className="tm-details-bar">{bar}</div>}
      <header className="tm-details-head">
        <span className="caption tm-muted">{item.title}</span>
        <h1 id={`booking-${item.id}`} className="display-m" ref={heading} tabIndex={-1}>
          {marking ? "Mark as booked" : "Edit booking"}
        </h1>
      </header>
      {meanwhile && (
        <p className="callout tm-booking-note is-meanwhile" role="alert">
          <Icon name="people" size={20} className="tm-booking-icon" />
          <span>
            {meanwhile.who === "You"
              ? `You changed it elsewhere while this was open, so nothing was saved. ${
                  meanwhile.theirs.length > 0
                    ? "Where that change and this one meet, that one shows. Check it and save again."
                    : "That change shows here. Save again to add this one."
                }`
              : `${meanwhile.who} changed it while this was open, so nothing was saved. ${
                  meanwhile.theirs.length > 0
                    ? "Where you both changed something, theirs shows. Check it and save again."
                    : "What they changed shows here. Save again to add yours."
                }`}
          </span>
        </p>
      )}

      <fieldset className="tm-booking-group">
        <legend className="title">When</legend>
        {changedBy("when")}
        {kind === "stay" && (
          <>
            {dateTime("start", "Check-in", "Time", "Check-in time")}
            {dateTime("end", "Check-out", "Time", "Check-out time")}
            {zoneSelect("startTz", "Time zone", "The hotel's time zone")}
          </>
        )}
        {kind === "move" && (
          <>
            {dateTime("start", "Departure", "Time", "Departure time")}
            {zoneSelect("startTz", "Departure time zone", "Departure time zone")}
            {dateTime("end", "Arrival", "Time", "Arrival time")}
            {zoneSelect("endTz", "Arrival time zone", "Arrival time zone")}
          </>
        )}
        {kind === "place" && (
          <>
            <Field label="Date" field="startDate" problem={problem("startDate")}>
              {(id) => (
                <input
                  id={id}
                  data-field="startDate"
                  className={`sv-field-input${draft.startDate ? "" : " is-empty"}`}
                  type="date"
                  value={draft.startDate}
                  aria-invalid={problem("startDate") ? true : undefined}
                  onChange={(e) => set({ startDate: e.target.value })}
                />
              )}
            </Field>
            <div className="tm-booking-pair">
              {(["startTime", "endTime"] as const).map((field) => (
                <Field
                  key={field}
                  label={field === "startTime" ? "From" : "Until"}
                  field={field}
                  problem={problem(field)}
                >
                  {(id) => (
                    <input
                      id={id}
                      data-field={field}
                      className={`sv-field-input${draft[field] ? "" : " is-empty"}`}
                      type="time"
                      value={draft[field]}
                      aria-invalid={problem(field) ? true : undefined}
                      onChange={(e) => set({ [field]: e.target.value })}
                    />
                  )}
                </Field>
              ))}
            </div>
            {zoneSelect("startTz", "Time zone", "Time zone")}
          </>
        )}
        <p className="caption tm-muted">
          {now ? `Now ${now}. ` : ""}
          {kind === "stay"
            ? "Times as the hotel gives them, in its time zone; leave them out for dates alone."
            : kind === "move"
              ? "Each time where it happens, in its own time zone."
              : "The time where it is, in its time zone; leave it out for the day alone."}
        </p>
      </fieldset>

      <fieldset className="tm-booking-group">
        <legend className="title">Price</legend>
        {changedBy("price")}
        <div className="tm-booking-pair is-money">
          <Field label="Amount" field="amount" problem={problem("amount")}>
            {(id) => (
              <input
                id={id}
                data-field="amount"
                className="sv-field-input"
                inputMode="decimal"
                autoComplete="off"
                value={draft.amount}
                aria-invalid={problem("amount") ? true : undefined}
                onChange={(e) => set({ amount: e.target.value })}
              />
            )}
          </Field>
          <Field label="Currency" field="currency" problem={null}>
            {(id) => (
              <Select
                id={id}
                field="currency"
                value={draft.currency}
                onChange={(currency) => set({ currency })}
                options={withValue(
                  currencies.map((c) => ({ value: c, label: c })),
                  draft.currency,
                )}
              />
            )}
          </Field>
        </div>
        <SegmentedControl
          label="The price is"
          value={draft.per}
          onChange={(per) => set({ per: per as Draft["per"] })}
          options={[
            { value: "group", label: "For the group" },
            { value: "person", label: "Per person" },
          ]}
        />
        {base.item.cost?.estimate && draft.amount.trim() && (
          <p className="caption tm-muted">An estimate so far: saved, it's the price as booked.</p>
        )}
      </fieldset>

      <fieldset className="tm-booking-group">
        <legend className="title">Paid</legend>
        {changedBy("paid")}
        {draft.lines.length === 0 && (
          <p className="callout tm-muted">Nothing paid yet: it shows as booked, not paid yet.</p>
        )}
        <ul className="tm-booking-lines">
          {draft.lines.map((line, n) => (
            <li key={line.key} className="tm-booking-line">
              <div className="tm-booking-line-head">
                <span className="caption tm-muted">Payment {n + 1}</span>
                <button
                  type="button"
                  className="tm-layer-close tm-booking-remove"
                  aria-label={`Remove payment ${n + 1}`}
                  onClick={() => set({ lines: draft.lines.filter((l) => l.key !== line.key) })}
                >
                  <Icon name="close" size={18} />
                </button>
              </div>
              <PaymentFields
                line={line}
                n={n + 1}
                who={whoOptions(line.by)}
                currencies={currencies}
                problem={problem}
                onChange={(patch) => setLine(line.key, patch)}
              />
            </li>
          ))}
        </ul>
        <div className="tm-booking-buttons">
          <Button
            variant="quiet"
            size="sm"
            icon="plus"
            onClick={() =>
              set({
                lines: [
                  ...draft.lines,
                  newLine({
                    by: draft.lines.some((l) => l.by === trip.you) ? "" : trip.you,
                    amount: "",
                    currency: draft.lines.at(-1)?.currency ?? draft.currency,
                    method: "",
                  }),
                ],
              })
            }
          >
            Add a payment
          </Button>
          {whole !== null && (
            <Button variant="quiet" size="sm" icon="check" onClick={paidInFull}>
              Paid in full by me
            </Button>
          )}
        </div>
        {whole !== null && draft.per === "person" && (
          <p className="caption tm-muted">
            In full: {paidMoney(whole, draft.currency)} for {count} {count === 1 ? "person" : "people"}.
          </p>
        )}
      </fieldset>

      <fieldset className="tm-booking-group">
        <legend className="title">Booking</legend>
        {(["provider", "ref", "url"] as const).map((field) => (
          <Field
            key={field}
            label={field === "provider" ? "Booked through" : field === "ref" ? "Reference" : "Link"}
            field={field}
            problem={problem(field)}
            theirs={changedBy(field)}
          >
            {(id) => (
              <input
                id={id}
                data-field={field}
                className="sv-field-input"
                autoComplete="off"
                {...(field === "url"
                  ? { type: "url", inputMode: "url" as const, placeholder: "https://" }
                  : field === "provider"
                    ? {
                        placeholder:
                          kind === "stay"
                            ? "Expedia, the hotel's own site"
                            : kind === "move"
                              ? "The airline, Trip.com, a station"
                              : "Klook, its own site",
                      }
                    : {})}
                maxLength={field === "url" ? 2000 : 100}
                value={draft[field]}
                aria-invalid={problem(field) ? true : undefined}
                onChange={(e) => set({ [field]: e.target.value })}
              />
            )}
          </Field>
        ))}
        <Field label="Booked by" field="bookedBy" problem={null} theirs={changedBy("bookedBy")}>
          {(id) => (
            <Select
              id={id}
              field="bookedBy"
              value={draft.bookedBy}
              onChange={(bookedBy) => set({ bookedBy })}
              options={[...whoOptions(draft.bookedBy), { value: "", label: "Nobody named" }]}
            />
          )}
        </Field>
        {policy ? (
          <div className="tm-booking-free">
            {changedBy("free")}
            {dateTimeFree()}
          </div>
        ) : (
          <div className="tm-booking-policy">
            <span className="sv-field-label">Cancellation</span>
            <p className="callout">{policyWords(base.item)}</p>
            <p className="caption tm-muted">A policy with several steps changes through your AI.</p>
          </div>
        )}
      </fieldset>

      <fieldset className="tm-booking-group">
        <legend className="title">The confirmation</legend>
        <input
          ref={fileInput}
          type="file"
          accept={CONFIRMATION_TYPES}
          hidden
          aria-label="Choose the confirmation"
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            e.target.value = "";
            if (file && file.size > MAX_BYTES) return setError(`${file.name} is over 20 MB. Try a smaller one.`);
            setConfirmation(file);
          }}
        />
        {confirmation ? (
          <div className="tm-booking-file">
            <Icon name="note" size={20} className="tm-booking-icon" />
            <span className="callout tm-booking-file-name">{confirmation.name}</span>
            <button
              type="button"
              className="tm-layer-close tm-booking-remove"
              aria-label={`Don't attach ${confirmation.name}`}
              onClick={() => setConfirmation(null)}
            >
              <Icon name="close" size={18} />
            </button>
          </div>
        ) : (
          <div className="tm-booking-buttons">
            <Button variant="quiet" size="sm" icon="plus" onClick={() => fileInput.current?.click()}>
              Attach the confirmation
            </Button>
          </div>
        )}
        <p className="caption tm-muted">
          A PDF or an image, up to 20 MB. It goes on its card, with its files, once this is saved.
        </p>
      </fieldset>

      {offline && (
        <p className="callout tm-booking-note" role="status">
          <Icon name="cloud" size={20} className="tm-booking-icon" />
          <span>No signal: saving needs signal. What you've filled in stays here until you're back online.</span>
        </p>
      )}
      {error && (
        <p className="callout tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      {problems.length > 0 && (
        <p className="callout tm-error" role="alert">
          {problems.length === 1 ? "One thing to fix above." : `${problems.length} things to fix above.`}
        </p>
      )}
      <div className="tm-booking-actions">
        <Button variant="primary" type="submit" disabled={busy || saving || offline}>
          {savingNow ? "Saving…" : "Save"}
        </Button>
        <Button variant="quiet" onClick={onClose}>
          Cancel
        </Button>
      </div>
      {unbookable && (
        <div className="tm-booking-unbook">
          <div className="tm-booking-buttons">
            <Button
              variant="quiet"
              size="sm"
              icon="replay"
              className="tm-not-booked"
              disabled={busy || saving || offline}
              onClick={notBooked}
            >
              {(busy || saving) && doing === "unbook" ? "Saving…" : "Not booked after all"}
            </Button>
          </div>
          <p className="caption tm-muted">
            Marked booked by mistake? It goes back to needing booking, and what's filled in here stays for next time. If
            it was cancelled, tell your AI.
          </p>
        </div>
      )}
    </form>
  );

  function dateTimeFree() {
    return (
      <>
        <div className="tm-booking-pair">
          <Field label="Free cancellation until" field="freeDate" problem={problem("freeDate")}>
            {(id) => (
              <input
                id={id}
                data-field="freeDate"
                className={`sv-field-input${draft.freeDate ? "" : " is-empty"}`}
                type="date"
                value={draft.freeDate}
                aria-invalid={problem("freeDate") ? true : undefined}
                onChange={(e) => set({ freeDate: e.target.value })}
              />
            )}
          </Field>
          <Field label="Time" field="freeTime" problem={null}>
            {(id) => (
              <input
                id={id}
                data-field="freeTime"
                className={`sv-field-input${draft.freeTime ? "" : " is-empty"}`}
                type="time"
                aria-label="Free cancellation until, time"
                value={draft.freeTime}
                onChange={(e) => set({ freeTime: e.target.value })}
              />
            )}
          </Field>
        </div>
        <p className="caption tm-muted">
          {base.item.booking?.cancellation?.steps.length === 0 && !changed(draft, base.draft, "free")
            ? "Non-refundable, as it is now. "
            : ""}
          In the booking's own time zone. Whoever booked it is reminded 3 days before.
        </p>
      </>
    );
  }
}

// Whether the item says its own time zone (a time with one).
const hasZone = (item: Item) => item.when?.precision === "exact" || item.when?.precision === "window";

// A policy the form doesn't change, in its own words, else its steps said.
function policyWords(item: Item): string {
  const policy = item.booking?.cancellation;
  if (!policy) return "";
  if (policy.text) return policy.text;
  return policy.steps
    .map((s) => {
      const by = s.until.length > 10 ? `${s.until.slice(0, 10)} ${s.until.slice(11, 16)}` : s.until;
      const back =
        s.refund === "full"
          ? "all back"
          : "percent" in s.refund
            ? `${s.refund.percent}% back`
            : `${paidMoney(s.refund.amount, s.refund.currency)} back`;
      return `${back} until ${by}`;
    })
    .join("; ");
}
